import { PROFESSION_PRESETS, DEFAULT_WEEKLY_RANGES } from '../../seed/professionPresets';
import { normalizeIsraeliPhone } from '../phone';
import { shekelsToAgorot } from '../money';
import { PLAN_CAPS, resolvePlan, type ResolvedPlan } from '../trial';
import { allowanceUsed } from '../reminders';
import {
  BUSINESS_TZ, DEFAULT_SETTINGS, fail, newId, ok, type Business, type BrandPhoto, type SlugAlias, type Id, type Locale, type Owner, type Profession, type Result,
  type Service, type Settings, type Staff, type TimeOff, type WeeklyHours,
} from '../types';
import { type Deps, findBusinessBySlug, loadBundle } from './context';

const HE_TO_LATIN: Record<string, string> = {
  א: 'a', ב: 'b', ג: 'g', ד: 'd', ה: 'h', ו: 'v', ז: 'z', ח: 'ch', ט: 't', י: 'y', כ: 'k', ך: 'k', ל: 'l', מ: 'm', ם: 'm',
  נ: 'n', ן: 'n', ס: 's', ע: 'a', פ: 'p', ף: 'f', צ: 'tz', ץ: 'tz', ק: 'k', ר: 'r', ש: 'sh', ת: 't',
};
const HE_LETTER = /[א-ת]/;

/** Guttural letters: a ו before them is read "o" (נועה -> noa); before other consonants "u" (סטודיו -> studio). */
const GUTTURAL = new Set(['א', 'ע', 'ה', 'ח']);

/** Transliterates one Hebrew word; ו/י/ה are read as vowels inside a word (נועה -> noa, ריסים -> risim, סטודיו -> studio). */
function translitWord(word: string): string {
  const chars = [...word];
  let out = '';
  chars.forEach((c, i) => {
    const prev = chars[i - 1];
    const next = chars[i + 1];
    const last = i === chars.length - 1;
    if (c === 'ו') out += i === 0 ? (next === 'ו' ? '' : 'v') : prev === 'ו' ? 'v' : last || next === undefined || GUTTURAL.has(next) || next === 'י' ? 'o' : 'u';
    else if (c === 'י') out += i === 0 ? 'y' : 'i';
    else if (c === 'ה' && last && i > 0) out += /[aeiou]$/.test(out) ? '' : 'a';
    else out += HE_TO_LATIN[c] ?? c;
  });
  return out.replace(/ii+/g, 'i').replace(/oo+/g, 'o').replace(/uu+/g, 'u');
}

export const SLUG_ALIAS_DAYS = 90;
export const MAX_BRAND_PHOTOS = 8;
export const MAX_PHOTO_BYTES = 200 * 1024;
const activeAliases = (b: Business, nowMs = Date.now()): SlugAlias[] => (b.slugAliases ?? []).filter((a) => Date.parse(a.until) > nowMs);

/** Decoded size in bytes of an image data URL (png/jpeg/webp/gif/svg), or undefined when it is not one. */
export function dataUrlBytes(dataUrl: string): number | undefined {
  const m = /^data:image\/(png|jpe?g|webp|gif|svg\+xml)(;[a-z0-9=.-]+)*,/i.exec(dataUrl);
  if (!m) return undefined;
  const body = dataUrl.slice(m[0].length);
  if (body.length === 0) return undefined;
  return /;base64$/i.test(m[0].slice(0, -1)) ? Math.floor((body.length * 3) / 4) - (body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0) : new TextEncoder().encode(decodeURIComponent(body)).length;
}

export const RESERVED_SLUGS: ReadonlySet<string> = new Set(['app', 'demo', 'admin', 'api', 'b', 'm', 'o', 'new', 'login', 'accessibility', 'privacy', 'terms', 'support', 'delete-account']);
export const isValidSlug = (slug: string): boolean => /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug) && slug.length >= 2 && slug.length <= 30 && !RESERVED_SLUGS.has(slug);

/** URL slug from a (possibly Hebrew) business name: "נועה ריסים" -> "noa-risim". */
export function slugify(name: string): string {
  const latin = name.normalize('NFC').replace(/[֑-ׇ]/g, '').replace(/["'׳״]/g, '').trim().split(/\s+/)
    .map((w) => [...w].some((c) => HE_LETTER.test(c)) ? translitWord(w) : w).join(' ').toLowerCase();
  const slug = latin.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30).replace(/-+$/, '');
  return slug.length >= 2 && !RESERVED_SLUGS.has(slug) ? slug : 'studio';
}

/** A slug is taken when it is any business's current slug or a still-active alias (optionally ignoring one business). */
async function slugInUse(d: Deps, slug: string, exceptBusinessId?: Id): Promise<boolean> {
  const nowMs = d.clock.now();
  return (await d.storage.repo('businesses').list({
    filter: (b) => b.id !== exceptBusinessId && (b.slug === slug || (b.slugAliases ?? []).some((a) => a.slug === slug && Date.parse(a.until) > nowMs)),
  })).length > 0;
}

async function uniqueSlug(d: Deps, base: string): Promise<string> {
  for (let i = 1; ; i++) {
    const candidate = i === 1 ? base : `${base}-${i}`;
    if (!(await slugInUse(d, candidate))) return candidate;
  }
}

export interface CreateOwnerInput {
  phone: string;
  businessName: string;
  profession: Profession;
  ownerName?: string;
  locale?: Locale;
}
export interface OwnerAccount {
  business: Business; owner: Owner; staff: Staff; services: Service[]; settings: Settings; weeklyHours: WeeklyHours[];
  /** Public booking link. */
  link: string;
}

function presetRows(d: Deps, businessId: Id, profession: Profession, staff: Staff[], now: string, firstSort: number) {
  const preset = PROFESSION_PRESETS[profession];
  const services: Service[] = preset.services.map((s, i) => ({
    id: newId(d.clock.now()), businessId, createdAt: now, updatedAt: now, name: s.name, category: s.category,
    durationMin: s.durationMin, bufferMin: s.bufferMin, priceAgorot: shekelsToAgorot(s.priceNis),
    ...(s.depositNis ? { depositAgorot: shekelsToAgorot(s.depositNis) } : {}), staffIds: [], active: true, sortOrder: firstSort + i,
  }));
  const hours: WeeklyHours[] = staff.flatMap((st) => DEFAULT_WEEKLY_RANGES.map((ranges, weekday) => ({
    id: newId(d.clock.now()), businessId, createdAt: now, updatedAt: now, staffId: st.id, weekday, ranges: ranges.map((r) => ({ ...r })),
  })));
  return { services, hours };
}

export function onboardingUseCases(d: Deps) {
  const nowIso = (): string => d.clock.nowIso();

  async function createOwnerAccount(input: CreateOwnerInput): Promise<Result<OwnerAccount, 'invalid_phone' | 'invalid_name' | 'not_authenticated' | 'owner_exists'>> {
    const phone = normalizeIsraeliPhone(input.phone);
    if (!phone.ok) return fail('invalid_phone');
    const name = input.businessName.trim();
    if (name.length < 2) return fail('invalid_name');
    if (d.auth.getSession()?.phone !== phone.e164) return fail('not_authenticated');
    if ((await d.storage.repo('owners').list({ where: { phone: phone.e164 } })).length > 0) return fail('owner_exists');

    const now = nowIso();
    const businessId = newId(d.clock.now());
    const business: Business = {
      id: businessId, businessId, createdAt: now, updatedAt: now, slug: await uniqueSlug(d, slugify(name)), name,
      profession: input.profession, tz: BUSINESS_TZ, locale: input.locale ?? 'he', brand: { color: 'violet' },
      plan: 'trial', trialStartedAt: now, topUpMessages: 0, phone: phone.e164, whatsapp: phone.e164,
    };
    const owner: Owner = { id: newId(d.clock.now()), businessId, createdAt: now, updatedAt: now, phone: phone.e164, name: input.ownerName?.trim() || name };
    const staff: Staff = { id: newId(d.clock.now()), businessId, createdAt: now, updatedAt: now, name: owner.name, active: true, colorKey: 1, sortOrder: 0, phone: phone.e164 };
    const settings: Settings = { ...DEFAULT_SETTINGS, id: businessId, businessId, createdAt: now, updatedAt: now };
    const { services, hours } = presetRows(d, businessId, input.profession, [staff], now, 0);

    await d.storage.transact(['businesses', 'owners', 'staff', 'settings', 'services', 'weeklyHours'], async (tx) => {
      await tx.put('businesses', business);
      await tx.put('owners', owner);
      await tx.put('staff', staff);
      await tx.put('settings', settings);
      for (const s of services) await tx.put('services', s);
      for (const h of hours) await tx.put('weeklyHours', h);
    });
    d.auth.linkSession(owner.id, businessId);
    return ok({ business, owner, staff, services, settings, weeklyHours: hours, link: d.links.booking(business.slug) });
  }

  /** Looks up the owner behind the signed-in session (returning owner on a new device/session). */
  async function restoreOwner(): Promise<Result<{ business: Business; owner: Owner }, 'no_session' | 'no_account'>> {
    const session = d.auth.getSession();
    if (!session) return fail('no_session');
    const [owner] = await d.storage.repo('owners').list({ where: { phone: session.phone } });
    const business = owner ? await d.storage.repo('businesses').get(owner.businessId) : undefined;
    if (!owner || !business) return fail('no_account');
    d.auth.linkSession(owner.id, business.id);
    return ok({ business, owner });
  }

  async function applyProfessionPreset(
    businessId: Id, profession: Profession, opts: { replace?: boolean } = {},
  ): Promise<Result<{ services: Service[]; weeklyHours: WeeklyHours[] }, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const now = nowIso();
    const staff = b.staff.filter((s) => s.active);
    const existing = await d.storage.repo('services').list({ where: { businessId } });
    const first = opts.replace ? 0 : existing.length;
    const { services, hours } = presetRows(d, businessId, profession, staff, now, first);
    await d.storage.transact(['businesses', 'services', 'weeklyHours'], async (tx) => {
      if (opts.replace) {
        for (const s of existing) await tx.delete('services', s.id);
        for (const h of b.weeklyHours) await tx.delete('weeklyHours', h.id);
      }
      for (const s of services) await tx.put('services', s);
      if (opts.replace || b.weeklyHours.length === 0) for (const h of hours) await tx.put('weeklyHours', h);
      await tx.put('businesses', { ...b.business, profession, updatedAt: now });
    });
    return ok({ services, weeklyHours: opts.replace || b.weeklyHours.length === 0 ? hours : b.weeklyHours });
  }

  // ------------------------------------------------------------ owner settings (thin, stamped writes)

  /**
   * Changes the public booking slug (lowercase a-z, 0-9, hyphens; 2-30 chars; unique). The old slug stays as an alias for
   * SLUG_ALIAS_DAYS (90) days: links and QR codes keep working and resolve to the current business.
   */
  async function updateBusinessSlug(businessId: Id, slug: string): Promise<Result<{ business: Business; link: string; aliases: SlugAlias[] }, 'business_not_found' | 'invalid_slug' | 'slug_taken'>> {
    const cur = await d.storage.repo('businesses').get(businessId);
    if (!cur) return fail('business_not_found');
    const next = slug.trim().toLowerCase();
    if (!isValidSlug(next)) return fail('invalid_slug');
    if (next === cur.slug) return ok({ business: cur, link: d.links.booking(cur.slug), aliases: activeAliases(cur, d.clock.now()) });
    if (await slugInUse(d, next, businessId)) return fail('slug_taken');
    const until = new Date(d.clock.now() + SLUG_ALIAS_DAYS * 86_400_000).toISOString();
    const aliases = [...activeAliases(cur, d.clock.now()).filter((a) => a.slug !== next && a.slug !== cur.slug), { slug: cur.slug, until }];
    const business: Business = { ...cur, slug: next, slugAliases: aliases, updatedAt: d.clock.nowIso() };
    await d.storage.repo('businesses').put(business);
    return ok({ business, link: d.links.booking(next), aliases });
  }

  /** Resolves a (possibly retired) slug to the business's current slug, so routes can redirect. */
  async function resolveSlug(slug: string): Promise<Result<{ slug: string; aliased: boolean }, 'not_found'>> {
    const b = await findBusinessBySlug(d, slug);
    return b ? ok({ slug: b.slug, aliased: b.slug !== slug }) : fail('not_found');
  }

  /** Active aliases (old slugs still resolving) of a business. */
  async function listSlugAliases(businessId: Id): Promise<SlugAlias[]> {
    const b = await d.storage.repo('businesses').get(businessId);
    return b ? activeAliases(b, d.clock.now()) : [];
  }

  // ------------------------------------------------------------ work photos (public page strip)
  const photoRows = (b: Business): BrandPhoto[] => [...(b.brand.photos ?? [])].sort((x, y) => x.order - y.order);
  async function savePhotos(b: Business, photos: BrandPhoto[]): Promise<Business> {
    const next: Business = { ...b, brand: { ...b.brand, photos: photos.map((p, i) => ({ ...p, order: i })) }, updatedAt: d.clock.nowIso() };
    await d.storage.repo('businesses').put(next);
    return next;
  }
  async function addBrandPhoto(businessId: Id, input: { dataUrl: string; alt?: string }): Promise<Result<{ business: Business; photo: BrandPhoto }, 'business_not_found' | 'invalid_image' | 'too_large' | 'too_many'>> {
    const b = await d.storage.repo('businesses').get(businessId);
    if (!b) return fail('business_not_found');
    const size = dataUrlBytes(input.dataUrl);
    if (size === undefined) return fail('invalid_image');
    if (size > MAX_PHOTO_BYTES) return fail('too_large');
    const rows = photoRows(b);
    if (rows.length >= MAX_BRAND_PHOTOS) return fail('too_many');
    const alt = input.alt?.trim();
    const photo: BrandPhoto = { id: newId(d.clock.now()), dataUrl: input.dataUrl, ...(alt ? { alt } : {}), order: rows.length };
    return ok({ business: await savePhotos(b, [...rows, photo]), photo });
  }
  async function removeBrandPhoto(businessId: Id, photoId: Id): Promise<Result<Business, 'business_not_found' | 'photo_not_found'>> {
    const b = await d.storage.repo('businesses').get(businessId);
    if (!b) return fail('business_not_found');
    const rows = photoRows(b);
    if (!rows.some((p) => p.id === photoId)) return fail('photo_not_found');
    return ok(await savePhotos(b, rows.filter((p) => p.id !== photoId)));
  }
  /** `orderedIds` must list every current photo exactly once. */
  async function reorderBrandPhotos(businessId: Id, orderedIds: Id[]): Promise<Result<Business, 'business_not_found' | 'invalid_order'>> {
    const b = await d.storage.repo('businesses').get(businessId);
    if (!b) return fail('business_not_found');
    const rows = photoRows(b);
    if (orderedIds.length !== rows.length || new Set(orderedIds).size !== rows.length || !orderedIds.every((id) => rows.some((p) => p.id === id))) return fail('invalid_order');
    return ok(await savePhotos(b, orderedIds.map((id) => rows.find((p) => p.id === id) as BrandPhoto)));
  }

  async function updateBusiness(businessId: Id, patch: Partial<Omit<Business, 'id' | 'businessId' | 'createdAt' | 'slug'>>): Promise<Result<Business, 'business_not_found'>> {
    const cur = await d.storage.repo('businesses').get(businessId);
    if (!cur) return fail('business_not_found');
    const next = { ...cur, ...patch, updatedAt: nowIso() };
    await d.storage.repo('businesses').put(next);
    return ok(next);
  }
  async function updateSettings(businessId: Id, patch: Partial<Omit<Settings, 'id' | 'businessId' | 'createdAt'>>): Promise<Result<Settings, 'business_not_found'>> {
    const cur = await d.storage.repo('settings').get(businessId);
    if (!cur) return fail('business_not_found');
    const next = { ...cur, ...patch, updatedAt: nowIso() };
    await d.storage.repo('settings').put(next);
    return ok(next);
  }
  async function saveService(input: Omit<Service, 'id' | 'createdAt' | 'updatedAt'> & { id?: Id }): Promise<Result<Service, 'invalid_service'>> {
    if (input.name.trim() === '' || input.durationMin <= 0 || input.priceAgorot < 0 || (input.depositAgorot ?? 0) < 0) return fail('invalid_service');
    const now = nowIso();
    const cur = input.id ? await d.storage.repo('services').get(input.id) : undefined;
    const row: Service = { ...input, id: input.id ?? newId(d.clock.now()), createdAt: cur?.createdAt ?? now, updatedAt: now };
    await d.storage.repo('services').put(row);
    return ok(row);
  }
  async function saveWeeklyHours(businessId: Id, staffId: Id, weekday: number, ranges: WeeklyHours['ranges']): Promise<Result<WeeklyHours, 'invalid_ranges'>> {
    const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
    const valid = sorted.every((r, i) => r.start < r.end && (i === 0 || (sorted[i - 1]?.end ?? '') <= r.start));
    if (!valid || weekday < 0 || weekday > 6) return fail('invalid_ranges');
    const now = nowIso();
    const [cur] = await d.storage.repo('weeklyHours').list({ where: { businessId }, filter: (w) => w.staffId === staffId && w.weekday === weekday });
    const row: WeeklyHours = { id: cur?.id ?? newId(d.clock.now()), businessId, staffId, weekday, ranges: sorted, createdAt: cur?.createdAt ?? now, updatedAt: now };
    await d.storage.repo('weeklyHours').put(row);
    return ok(row);
  }
  async function saveTimeOff(input: Omit<TimeOff, 'id' | 'createdAt' | 'updatedAt'> & { id?: Id }): Promise<Result<TimeOff, 'invalid_range'>> {
    if (input.end <= input.start) return fail('invalid_range');
    const now = nowIso();
    const row: TimeOff = { ...input, id: input.id ?? newId(d.clock.now()), createdAt: now, updatedAt: now };
    await d.storage.repo('timeOff').put(row);
    return ok(row);
  }
  const deleteTimeOff = (id: Id): Promise<void> => d.storage.repo('timeOff').delete(id);

  async function addStaff(businessId: Id, name: string): Promise<Result<Staff, 'business_not_found' | 'plan_staff_limit' | 'invalid_name'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    if (name.trim() === '') return fail('invalid_name');
    if (b.staff.filter((s) => s.active).length >= b.plan.caps.maxStaff) return fail('plan_staff_limit');
    const now = nowIso();
    const staff: Staff = { id: newId(d.clock.now()), businessId, createdAt: now, updatedAt: now, name: name.trim(), active: true, colorKey: (b.staff.length % 8) + 1, sortOrder: b.staff.length };
    await d.storage.transact(['staff', 'weeklyHours'], async (tx) => {
      await tx.put('staff', staff);
      for (const [weekday, ranges] of DEFAULT_WEEKLY_RANGES.entries()) {
        await tx.put('weeklyHours', { id: newId(d.clock.now()), businessId, createdAt: now, updatedAt: now, staffId: staff.id, weekday, ranges: ranges.map((r) => ({ ...r })) });
      }
    });
    return ok(staff);
  }

  // ------------------------------------------------------------ plan / allowance

  async function getPlanStatus(businessId: Id): Promise<Result<PlanStatus, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const messages = await d.storage.repo('messages').list({ where: { businessId } });
    const used = allowanceUsed(messages, d.clock.now(), b.business.tz);
    return ok({ plan: b.plan, used, remaining: Math.max(0, b.plan.allowance - used), plans: PLAN_CAPS, resolvedAt: nowIso() });
  }
  async function readPlan(businessId: Id): Promise<ResolvedPlan | undefined> {
    const business = await d.storage.repo('businesses').get(businessId);
    return business ? resolvePlan(business, d.clock.now()) : undefined;
  }

  return { createOwnerAccount, restoreOwner, applyProfessionPreset, updateBusiness, updateBusinessSlug, resolveSlug, listSlugAliases, addBrandPhoto, removeBrandPhoto, reorderBrandPhotos, updateSettings, saveService, saveWeeklyHours, saveTimeOff, deleteTimeOff, addStaff, getPlanStatus, getPlan: readPlan };
}

export interface PlanStatus {
  plan: ResolvedPlan;
  /** Automatic messages counted this month. */
  used: number;
  remaining: number;
  plans: typeof PLAN_CAPS;
  resolvedAt: string;
}
